import { useEffect, useRef, useState } from "react";
import React from "react";
import "./style.scss";
import Button from "@components/Button";
import Dropdown from "@components/Dropdown";
import Chevron from "@assets/chevron.svg?react";

const PRICE_TABLE = {
  "Vuse Go": { normal: 11.5, tier5_9: 9.77, tier10_30: 8.05, minQty: 5 },
  "Vuse Pods": { normal: 11.95, tier5_9: 10.16, tier10_30: 8.37, minQty: 5 },
  "Vuse Ultra Pods": {
    normal: 13.95,
    tier5_9: 11.9,
    tier10_30: 9.77,
    minQty: 5,
  },
};

const MIN_QTY = 0;
const MAX_QTY = 30;
const DEFAULT_MARKE = "Vuse Ultra Pods";

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
const getPrices = (marke) => PRICE_TABLE[marke] ?? PRICE_TABLE[DEFAULT_MARKE];
const getMinQty = (marke) => getPrices(marke).minQty;

export default function Calculator() {
  const [marke, setMarke] = useState(DEFAULT_MARKE);
  const [count, setCount] = useState(() => getMinQty(DEFAULT_MARKE));
  const [calcSavings, setCalcSavings] = useState(0);
  const [markeDropdownOpen, setMarkeDropdownOpen] = useState(false);
  const markeDropdownRef = useRef(null);
  const markeOptions = ["Vuse Ultra Pods", "Vuse Pods", "Vuse Go"];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        markeDropdownRef.current &&
        !markeDropdownRef.current.contains(event.target)
      ) {
        setMarkeDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const prices = getPrices(marke);
    const minQty = prices.minQty;
    const qty = clamp(Number(count) || MIN_QTY, MIN_QTY, MAX_QTY);

    let discounted = prices.normal;
    if (qty >= 10) discounted = prices.tier10_30;
    else if (qty >= minQty) discounted = prices.tier5_9;

    const savings = (prices.normal - discounted) * qty;

    setCalcSavings(Math.max(0, Math.round(savings * 100) / 100));
  }, [count, marke]);

  return (
    <div className="calculator-component">
      <div className="calc-wrapper">
        <p className="calc-copy">Wie viele Pods benötigst du pro&nbsp;Monat?</p>

        <div className="calc-inputs-wrapper">
          <div className="calc-inputs">
            <p
              className="icon"
              onClick={() => setCount((c) => clamp(c - 1, MIN_QTY, MAX_QTY))}
            >
              -
            </p>
            <p className="calc-count">{count}</p>
            <p
              className="icon"
              onClick={() => setCount((c) => clamp(c + 1, MIN_QTY, MAX_QTY))}
            >
              +
            </p>
          </div>

          <p className="multiply">X</p>

          <div className="calc-dropdown-wrapper" ref={markeDropdownRef}>
            <Dropdown
              className={`calc-dropdown ${markeDropdownOpen ? "dropdown-open" : ""}`}
              absolute={true}
              summary={
                <div
                  className="marke-wrapper"
                  onClick={() => setMarkeDropdownOpen((o) => !o)}
                >
                  <h2 className="marke">{marke}</h2>
                  <Chevron
                    className={`chevron ${markeDropdownOpen ? "chevron-rotated" : ""}`}
                  />
                </div>
              }
              open={markeDropdownOpen}
              details={
                <div
                  className={`dropdown-options ${markeDropdownOpen ? "dropdown-options-shown" : ""}`}
                >
                  {markeOptions.map((option) => (
                    <button
                      type="button"
                      key={option}
                      onClick={() => {
                        setMarkeDropdownOpen(false);
                        setMarke(option);
                        setCount((c) => clamp(c, MIN_QTY, MAX_QTY));
                      }}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              }
            />
          </div>
        </div>

        <div className="calc-savings">
          Deine Ersparnis pro Monat:
          <span>€ {calcSavings.toFixed(2)}</span>
        </div>
      </div>

      <div className="calc-benefits-wrapper">
        <h2 className="calc-benefits-head">Vorteile eines Abonnements</h2>
        <p className="calc-benefits-copy">
          Spare bis zu 30% auf deine Lieblings Vuse Flavour mit dem Premium
          <strong>Abo </strong>
          by&nbsp;Vuse.
        </p>
        <p className="calc-benefits-copy">
          Wähle das Abo, das dir mehr gibt –{" "}
          <strong>
            weniger&nbsp;Aufwand, mehr&nbsp;Komfort, mehr&nbsp;Vorteile.
          </strong>
        </p>
        <Button
          href={`${import.meta.env.VITE_VUSE_URL}/vorteils-abo/?utm_source=IC&utm_medium=Dashboard&utm_campaign=PremiumAbo`}
          btnClass="ultra-insider"
        >
          Zum PremiumAbo
        </Button>
      </div>
    </div>
  );
}
